import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { SectionHeader } from "../../GlobalComponents/SectionHeader/SectionHeader";
import Icon from "../../Icon/Icon";
import { ReportFilters } from "./components/ReportFilters";
import { PivotFields } from "./components/PivotFields";
import { PivotGrid } from "./components/PivotGrid";
import { PivotChart } from "./components/PivotChart";
import { SavedViewsBar } from "./components/SavedViewsBar";
import { seriesOptions, TOTAL_SERIES } from "./utils/pivotToChartData";
import { serializeView, deserializeView } from "./utils/viewState";
import { useBuilderDims } from "./hooks/useBuilderDims";
import { computePivot, pivotHasData } from "./utils/pivot";
import { fetchPivotData, MAX_RECORDS, pivotMeasureOf } from "./utils/materializeView";
import { exportPivotToExcel } from "./utils/exportPivot";
import { VALUE_COUNT, VALUE_MODES } from "./utils/formatValue";
import {
  DEFAULT_STAT,
  DURATION_MEASURES,
  isDurationMeasure,
  MEASURE_COUNT,
  MEASURE_KEYS,
  measureLabel,
  STATS,
} from "./utils/measures";
import "./Builder.scss";

const EMPTY_ASSIGNMENTS = { rows: [], cols: [], filters: [] };

// Same set Dashboardv2 offers, rendered by the same DynamicChart.
const CHART_TYPES = ["pie", "donut", "bar", "line", "nightingale", "rose"];

/**
 * Builder - PivotTable over Reports, backed by EQL.
 *
 * EQL's `aggregate` groups by exactly ONE field, so the Builder picks a path:
 *
 *   1 axis, aggregatable, no pivot filters -> POST aggregate.
 *      One request, exact over the whole filtered set, no 1000-row cap, and
 *      `depth` collapses hierarchical catalog values server-side.
 *
 *   anything else (a rows x cols cross, several fields per zone, or a pivot
 *   Filters zone) -> POST raw rows (paginated) and cross them client-side.
 *      Cheaper than N+1 aggregate calls, since each aggregate call re-scans
 *      every filtered row in Python (no SQL GROUP BY).
 */
const Builder = () => {
  const [t] = useTranslation("exports");

  // Dimension machinery (fields, labels, catalog discovery + depth) lives in a
  // shared hook so the Custom Dashboards piece editor reuses it verbatim.
  const {
    dimByKey,
    catalogDims,
    fields,
    fieldTitle,
    labelsRef,
    depthByCatalog,
    setDepthByCatalog,
    catalogDepth,
    catalogLabelsVersion,
  } = useBuilderDims();

  const [appliedFilters, setAppliedFilters] = useState(undefined);
  const [applied, setApplied] = useState(false);

  const [assignments, setAssignments] = useState(EMPTY_ASSIGNMENTS);
  const [filterValues, setFilterValues] = useState({});

  // View options (mirror Dashboardv2: table <-> chart, and the chart type)
  const [view, setView] = useState("table"); // "table" | "chart"
  const [chartType, setChartType] = useState("pie");
  const [seriesKey, setSeriesKey] = useState(TOTAL_SERIES);
  // Cell values are Count or Percentage (of the grand total) — one or the other.
  const [valueMode, setValueMode] = useState(VALUE_COUNT);
  // What cells hold: Count, or a statistic of a status duration (measures.js).
  const [measure, setMeasure] = useState(MEASURE_COUNT);
  const [stat, setStat] = useState(DEFAULT_STAT);
  const isDuration = isDurationMeasure(measure);
  const durationDef = DURATION_MEASURES.find((m) => m.key === measure);

  const [loading, setLoading] = useState(false);
  const [aggPivot, setAggPivot] = useState(null); // aggregate-mode grid
  const [records, setRecords] = useState([]); // raw-mode records
  const [meta, setMeta] = useState(null); // { mode, total, truncated }

  const requestId = useRef(0);

  const { rows, cols, filters: filterZone } = assignments;

  // Identity of the data request, as primitives only. Every dep must be a
  // string/number: objects rebuilt each render (labels, dims) would retrigger
  // the effect forever. `filterValues` is excluded on purpose too — the pivot
  // Filters zone is applied client-side and must not refetch.
  const rowsKey = rows.join("|");
  const colsKey = cols.join("|");
  const filterZoneKey = filterZone.join("|");
  const filtersKey = JSON.stringify(appliedFilters ?? null);
  const catalogsKey = catalogDims.map((d) => d.key).join("|");
  // Only the depth of catalogs actually in play should retrigger the query.
  const usedDimKeys = [...new Set([...rows, ...cols, ...filterZone])];
  const depthKey = usedDimKeys
    .filter((k) => dimByKey[k]?.isCatalog)
    .map((k) => `${k}:${catalogDepth(k)}`)
    .join("|");
  // Durations need the status fields (and the raw route); every duration is
  // computed per record at once, so switching between them or the statistic
  // only re-crosses. Only toggling Count <-> duration refetches.
  const durationsKey = isDuration ? 1 : 0;

  useEffect(() => {
    if (!applied) return;

    if (!rows.length && !cols.length) {
      setAggPivot(null);
      setRecords([]);
      setMeta(null);
      return;
    }

    const id = ++requestId.current;
    const stale = () => id !== requestId.current;

    (async () => {
      setLoading(true);
      try {
        // The route choice (server-side aggregate vs client-side raw cross) and
        // closed-type scoping live in fetchPivotData, shared with Custom
        // Dashboards. Raw mode returns records so the Filters zone stays a memo.
        const res = await fetchPivotData(
          { assignments, appliedFilters, depthByCatalog, measure },
          { dimByKey, labels: labelsRef.current }
        );
        if (stale()) return;

        if (res.mode === "aggregate") {
          setRecords([]);
          setAggPivot(res.pivot);
        } else {
          setAggPivot(null);
          setRecords(res.records);
        }
        setMeta(res.meta);
      } catch (e) {
        console.error("Builder: EQL query failed", e);
        if (stale()) return;
        setAggPivot(null);
        setRecords([]);
        setMeta(null);
      } finally {
        if (!stale()) setLoading(false);
      }
    })();
    // catalogLabelsVersion: catalog locales and trees load async, so re-derive labels
    // (re-flatten / re-aggregate) once they arrive if a pivot is already applied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, filtersKey, rowsKey, colsKey, filterZoneKey, depthKey, catalogsKey, catalogLabelsVersion, durationsKey]);

  // The cross itself is pure: aggregate mode already has its grid; raw mode
  // crosses the fetched records, and the Filters zone applies here.
  const pivot = useMemo(() => {
    if (!applied || (!rows.length && !cols.length)) return null;
    if (meta?.mode === "aggregate") return aggPivot;
    if (!records.length) return null;
    return computePivot(records, rows, cols, filterValues, pivotMeasureOf({ measure, stat }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, meta, aggPivot, records, rowsKey, colsKey, filterValues, measure, stat]);

  const handleApply = (filters) => {
    setAppliedFilters(filters);
    setApplied(true);
  };

  const handleAssign = (fieldKey, zone) => {
    setAssignments((prev) => {
      const next = {
        rows: prev.rows.filter((k) => k !== fieldKey),
        cols: prev.cols.filter((k) => k !== fieldKey),
        filters: prev.filters.filter((k) => k !== fieldKey),
      };
      if (zone === "rows" || zone === "cols" || zone === "filters") {
        next[zone] = [...next[zone], fieldKey];
      }
      return next;
    });
    if (zone !== "filters") {
      setFilterValues((prev) => {
        if (!(fieldKey in prev)) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    }
  };

  const handleToggleFilterValue = (field, value, allOptions) => {
    setFilterValues((prev) => {
      const current =
        prev[field] && prev[field].size > 0
          ? new Set(prev[field])
          : new Set(allOptions);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [field]: current };
    });
  };

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    exportPivotToExcel(pivot, {
      fieldTitle,
      totalLabel: t("builder.total"),
      grandTotalLabel: t("builder.grand_total"),
      countLabel: measureLabel(t, pivot?.measure),
      valueMode,
      sheetName: t("builder.sheet_name"),
      filename: `${t("builder.file_prefix")}-${stamp}.xlsx`,
    });
  };

  // Only a cross exposes pickable series; otherwise there is a single one.
  const series = seriesOptions(pivot, t("builder.total"));
  const chartTitle = [...rows, ...cols].map(fieldTitle).join(" × ");

  // A query ran (meta set) but matched nothing: show an explicit empty state
  // instead of the "configure & apply" hint, which reads like nothing happened.
  const hasData = pivotHasData(pivot);
  const showEmpty = applied && !loading && !!meta && !hasData;

  const getState = () =>
    serializeView({
      appliedFilters,
      assignments,
      filterValues,
      depthByCatalog,
      view,
      chartType,
      seriesKey,
      valueMode,
      measure,
      stat,
    });

  const handleLoadView = (state) => {
    const s = deserializeView(state);
    setAppliedFilters(s.appliedFilters);
    setAssignments(s.assignments);
    setFilterValues(s.filterValues);
    setDepthByCatalog(s.depthByCatalog);
    setView(s.view);
    setChartType(s.chartType);
    setSeriesKey(s.seriesKey);
    setValueMode(s.valueMode);
    setMeasure(s.measure);
    setStat(s.stat);
    setApplied(true); // triggers the data effect with the restored layout
  };

  const handleChangeCatalogDepth = (key, level) =>
    setDepthByCatalog((prev) => ({ ...prev, [key]: level }));

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title={t("builder.title")}
        subtitle={t("builder.subtitle")}
        icon={<Icon name="bold_chart_square" size={48} />}
      />

      <SavedViewsBar getState={getState} onLoad={handleLoadView} />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <ReportFilters loading={loading} onApply={handleApply} />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9 }}>
          <PivotFields
            fields={fields}
            assignments={assignments}
            onAssign={handleAssign}
            records={records}
            filterValues={filterValues}
            catalogDepth={catalogDepth}
            onChangeCatalogDepth={handleChangeCatalogDepth}
            onToggleFilterValue={handleToggleFilterValue}
          />
        </Grid>
        <Grid size={12}>
          {meta && (
            <div className="builder-resultbar">
              <span>
                {t("builder.reports_count", { count: meta.total })}
                <span className="builder-resultbar__mode">
                  {" · "}
                  {t(`builder.mode_${meta.mode}`)}
                  {durationDef && (
                    <>
                      {" · "}
                      {t(
                        durationDef.currentOnly
                          ? "builder.measure_hint_closed_only"
                          : "builder.measure_hint",
                        {
                          from: labelsRef.current.statusLabel(durationDef.from),
                          to: labelsRef.current.statusLabel(durationDef.to),
                        }
                      )}
                    </>
                  )}
                </span>
                {meta.truncated && (
                  <span className="builder-resultbar__warn">
                    {" "}
                    {t("builder.truncated", { max: MAX_RECORDS })}
                  </span>
                )}
              </span>
              <div className="builder-viewbar">
                <div className="builder-toggle">
                  {["table", "chart"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`builder-toggle__btn ${
                        view === v ? "--active" : ""
                      }`}
                      onClick={() => setView(v)}
                    >
                      {t(`builder.view_${v}`)}
                    </button>
                  ))}
                </div>

                <select
                  className="builder-select"
                  value={measure}
                  onChange={(e) => setMeasure(e.target.value)}
                  aria-label={t("builder.measure")}
                >
                  {MEASURE_KEYS.map((m) => (
                    <option key={m} value={m}>
                      {t(`builder.measure_${m}`)}
                    </option>
                  ))}
                </select>

                {isDuration ? (
                  <select
                    className="builder-select"
                    value={stat}
                    onChange={(e) => setStat(e.target.value)}
                    aria-label={t("builder.stat")}
                  >
                    {STATS.map((st) => (
                      <option key={st} value={st}>
                        {t(`builder.stat_${st}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="builder-toggle">
                    {VALUE_MODES.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`builder-toggle__btn ${
                          valueMode === m ? "--active" : ""
                        }`}
                        onClick={() => setValueMode(m)}
                      >
                        {t(`builder.value_${m}`)}
                      </button>
                    ))}
                  </div>
                )}

                {view === "chart" && (
                  <>
                    <select
                      className="builder-select"
                      value={chartType}
                      onChange={(e) => setChartType(e.target.value)}
                    >
                      {CHART_TYPES.map((ct) => (
                        <option key={ct} value={ct}>
                          {t(`builder.chart_${ct}`)}
                        </option>
                      ))}
                    </select>

                    {series.length > 0 && (
                      <select
                        className="builder-select"
                        value={seriesKey}
                        onChange={(e) => setSeriesKey(e.target.value)}
                      >
                        {series.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                )}

                <button
                  type="button"
                  className="builder-btn builder-btn--primary builder-btn--compact"
                  onClick={handleExport}
                  disabled={!hasData || loading}
                >
                  {t("builder.export_excel")}
                </button>
              </div>
            </div>
          )}
          {loading ? (
            <Box className="builder-panel builder-panel--hint dyTheme1 dyBorder1 rounded">
              <span className="builder-empty">{t("builder.loading")}</span>
            </Box>
          ) : showEmpty ? (
            <Box className="builder-panel builder-panel--empty dyTheme1 dyBorder1 rounded">
              <Icon
                name="outline_format_search"
                size={44}
                className="builder-empty__icon"
              />
              <span className="builder-empty__title">
                {t("builder.no_results_title")}
              </span>
              <span className="builder-empty__hint">
                {t("builder.no_results_hint")}
              </span>
            </Box>
          ) : view === "chart" && pivot ? (
            <PivotChart
              pivot={pivot}
              type={chartType}
              seriesKey={seriesKey}
              title={chartTitle}
              valueMode={valueMode}
            />
          ) : (
            <PivotGrid
              pivot={pivot}
              fieldTitle={fieldTitle}
              valueMode={valueMode}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Builder;
